import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useAuditApprovals } from "services/hooks/auditApprovals";
import { useOrganisations } from "services/hooks/rentals";
import { AUDIT_ACTION_LABELS } from "../constants";

const AuditLogTab = () => {
  const role = useSelector((state) => state.role);
  const isAdmin = role === "admin";
  const { auditEvents, isLoadingAuditEvents, auditEventsError, fetchAuditEvents, fetchAuditEventDetail } =
    useAuditApprovals();
  const { organisations } = useOrganisations({ enabled: isAdmin });

  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [action, setAction] = useState("");
  const [actorUserId, setActorUserId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orgFilter, setOrgFilter] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadEvents = () => {
    const filters = { limit: 50, offset: 0 };
    if (entityType) filters.entity_type = entityType;
    if (entityId) filters.entity_id = entityId;
    if (action) filters.action = action;
    if (actorUserId) filters.actor_user_id = actorUserId;
    if (fromDate) filters.from = fromDate;
    if (toDate) filters.to = toDate;
    if (isAdmin && orgFilter) filters.organization_id = orgFilter;
    fetchAuditEvents(filters);
  };

  React.useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    const result = await fetchAuditEventDetail(row.id);
    setSelectedEvent(result.success ? result.data : row);
    setDetailLoading(false);
  };

  const rows = auditEvents.results ?? [];

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <TextField size="small" label="Entity type" value={entityType} onChange={(e) => setEntityType(e.target.value)} placeholder="landlord_payout" />
        <TextField size="small" label="Entity ID" value={entityId} onChange={(e) => setEntityId(e.target.value)} />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Action</InputLabel>
          <Select value={action} label="Action" onChange={(e) => setAction(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.keys(AUDIT_ACTION_LABELS).map((a) => (
              <MenuItem key={a} value={a}>{AUDIT_ACTION_LABELS[a]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" label="Actor user ID" value={actorUserId} onChange={(e) => setActorUserId(e.target.value)} />
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={toDate} onChange={(e) => setToDate(e.target.value)} />
        {isAdmin && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Organization</InputLabel>
            <Select value={orgFilter} label="Organization" onChange={(e) => setOrgFilter(e.target.value)}>
              <MenuItem value="">All orgs</MenuItem>
              {(organisations ?? []).map((org) => (
                <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="contained" onClick={loadEvents}>Search</Button>
      </Box>

      {isLoadingAuditEvents ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : auditEventsError ? (
        <Alert severity="error">Failed to load audit events.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell align="right">Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No audit events found.</TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.created_at ? moment(row.created_at).format("MMM D, YYYY HH:mm") : "—"}
                    </TableCell>
                    <TableCell>{row.actor_display_name ?? `User #${row.actor_user_id}`}</TableCell>
                    <TableCell>
                      <Chip size="small" label={AUDIT_ACTION_LABELS[row.action] ?? row.action} />
                    </TableCell>
                    <TableCell>{row.summary ?? row.entity_label ?? "—"}</TableCell>
                    <TableCell>
                      {row.entity_type}
                      {row.entity_id ? ` #${row.entity_id}` : ""}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openDetail(row)}>View</Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Audit event #{selectedEvent?.id}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <CircularProgress />
          ) : selectedEvent ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>{selectedEvent.summary}</Typography>
              {selectedEvent.related_approval_request_id && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Related approval request #{selectedEvent.related_approval_request_id}
                </Alert>
              )}
              {selectedEvent.changes && (
                <>
                  <Typography variant="subtitle2">Changes</Typography>
                  <Box component="pre" sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1, fontSize: 12, overflow: "auto" }}>
                    {JSON.stringify(selectedEvent.changes, null, 2)}
                  </Box>
                </>
              )}
              {selectedEvent.before_snapshot && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Before</Typography>
                  <Box component="pre" sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1, fontSize: 12, overflow: "auto" }}>
                    {JSON.stringify(selectedEvent.before_snapshot, null, 2)}
                  </Box>
                </>
              )}
              {selectedEvent.after_snapshot && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>After</Typography>
                  <Box component="pre" sx={{ bgcolor: "grey.100", p: 2, borderRadius: 1, fontSize: 12, overflow: "auto" }}>
                    {JSON.stringify(selectedEvent.after_snapshot, null, 2)}
                  </Box>
                </>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogTab;
